import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, CheckCircle, Clock, Filter, Search, Shield } from 'lucide-react';
import { supabase } from '../lib/clientPublic';

interface LoginAttempt {
  id: string;
  email: string;
  user_id: string | null;
  success: boolean;
  error_type: string | null;
  error_message: string | null;
  ip_address: string | null;
  user_agent: string | null;
  attempted_at: string;
}

interface LoginStats {
  failed24h: number;
  total24h: number;
}

const AdminLoginAttemptsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
  const [stats, setStats] = useState<LoginStats>({ failed24h: 0, total24h: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [emailFilter, setEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [timeRange, setTimeRange] = useState<'24h' | '48h' | '7d' | '30d' | 'all'>('48h');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (user === null) return;
    if (!user?.isSuperAdmin) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchLoginAttempts();
  }, [user?.isSuperAdmin, statusFilter, timeRange, navigate]);

  const fetchLoginAttempts = async () => {
    setLoading(true);
    setError('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/get-login-attempts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          email: emailFilter || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          timeRange: timeRange !== 'all' ? timeRange : undefined,
          limit: 100,
          offset: 0,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`Failed to fetch login attempts: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Login attempts result:', result);

      if (result.success) {
        setAttempts(result.data.attempts);
        setStats(result.data.stats);
        setError(''); // Clear any previous errors
      } else {
        throw new Error(result.error || 'Failed to fetch login attempts');
      }
    } catch (err: any) {
      console.error('Error fetching login attempts:', err);
      setError(err.message || 'Fehler beim Laden der Login-Versuche');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchLoginAttempts();
  };

  const getErrorTypeLabel = (errorType: string | null): string => {
    if (!errorType) return 'Unbekannt';

    const labels: { [key: string]: string } = {
      'invalid_credentials': 'Falsche Anmeldedaten',
      'email_not_confirmed': 'E-Mail nicht bestätigt',
      'account_disabled': 'Konto deaktiviert',
      'session_error': 'Sitzungsfehler',
      'network_error': 'Netzwerkfehler',
      'unknown_error': 'Unbekannter Fehler',
    };

    return labels[errorType] || errorType;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (!user?.isSuperAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
          >
            ← Zurück zum Dashboard
          </button>
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Login-Versuche</h1>
          </div>
          <p className="mt-2 text-gray-600">
            Überwachung aller Anmeldeversuche und Fehleranalyse
          </p>
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              Die Statistik erfasst einzelne Anmeldeversuche, nicht die Anzahl erfolgreich oder erfolglos registrierter Teilnehmer. Insbesondere Anmeldeversuche vor der E-Mail-Bestätigung werden als fehlgeschlagen gezählt, auch wenn der Teilnehmer den Kurs anschließend erfolgreich absolviert.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Gesamt (48h)</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total24h}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Fehlgeschlagen (48h)</p>
                <p className={`text-2xl font-bold ${stats.failed24h > 10 ? 'text-red-600' : 'text-gray-900'}`}>
                  {stats.failed24h}
                </p>
              </div>
              <AlertCircle className={`h-8 w-8 ${stats.failed24h > 10 ? 'text-red-600' : 'text-gray-400'}`} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Erfolgsrate (48h)</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.total24h > 0 ? Math.round(((stats.total24h - stats.failed24h) / stats.total24h) * 100) : 0}%
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
            >
              <Filter className="h-5 w-5" />
              {showFilters ? 'Filter ausblenden' : 'Filter anzeigen'}
            </button>
          </div>

          {showFilters && (
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail
                  </label>
                  <input
                    type="text"
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    placeholder="Suche nach E-Mail..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Alle</option>
                    <option value="success">Erfolgreich</option>
                    <option value="failed">Fehlgeschlagen</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zeitraum
                  </label>
                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="24h">Letzte 24 Stunden</option>
                    <option value="48h">Letzte 48 Stunden</option>
                    <option value="7d">Letzte 7 Tage</option>
                    <option value="30d">Letzte 30 Tage</option>
                    <option value="all">Alle</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={handleSearch}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center justify-center gap-2"
                  >
                    <Search className="h-4 w-4" />
                    Suchen
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Lade Login-Versuche...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Zeitpunkt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      E-Mail
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fehlertyp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User Agent
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attempts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                        Keine Login-Versuche gefunden
                      </td>
                    </tr>
                  ) : (
                    attempts.map((attempt) => (
                      <tr key={attempt.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(attempt.attempted_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {attempt.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {attempt.success ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Erfolgreich
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Fehlgeschlagen
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {attempt.success ? '-' : getErrorTypeLabel(attempt.error_type)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={attempt.user_agent || ''}>
                          {attempt.user_agent || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLoginAttemptsPage;
